import React from 'react';
import { useParams } from 'react-router';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import i18n from '../../utilities/i18n';
import SensorHeader from './sensorComponents/SensorHeader';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deviceByKeyQueryOptions,
  sensorQueryOptions,
} from '../../queryOptions';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import DynamicTable, { ColumnConfig } from '../table/DynamicTable';
import { UserNote } from 'adc-sirius-module-common';
import DateCellRenderer from '../table/DateCellRenderer';
import CreateUserNoteModal from '../modal/CreateUserNoteModal';
import {
  createSensorNoteMutateOptions,
  sensorOverridesBySensorKeyQueryOptions,
} from '../../queryOptions/sensor';
import {
  useResolvedDeviceHierarchy,
  useSyncCurrentOrganization,
} from '../../hooks';
import { sensorCapabilitiesQueryOptions } from '../../queryOptions/capability';

/**
 * Sensor component that displays the details of a specific sensor.
 * @returns {React.JSX.Element}
 */
export default function Sensor() {
  const queryClient = useQueryClient();

  const { sensorKey } = useParams();

  const sensor = useQuery(sensorQueryOptions({ key: sensorKey }));
  const sensorOverrides = useQuery(
    sensorOverridesBySensorKeyQueryOptions({ key: sensorKey }),
  );
  const { data: capabilities } = useQuery(
    sensorCapabilitiesQueryOptions({ key: sensorKey }),
  );
  const device = useQuery(
    deviceByKeyQueryOptions(sensor.data?.deviceKey || ''),
  );

  const { organizationKey } = useResolvedDeviceHierarchy(device.data);

  useSyncCurrentOrganization(organizationKey);

  const errorMessage = useErrorMessageOnEntityView(
    sensor.error || sensorOverrides.error,
    'Page_Sensor.load_error',
  );

  const createNote = useMutation(
    createSensorNoteMutateOptions({
      onSuccess: (updatedSensor, variables) => {
        // update only the notes in the cached sensor data
        queryClient.setQueryData(
          sensorQueryOptions({ key: variables.sensorKey }).queryKey,
          (cachedSensor) => {
            if (!cachedSensor) {
              return updatedSensor;
            }

            return {
              ...cachedSensor,
              userNotes: updatedSensor.userNotes || cachedSensor.userNotes,
              updatedOn: updatedSensor.updatedOn || cachedSensor.updatedOn,
              updatedBy: updatedSensor.updatedBy || cachedSensor.updatedBy,
              updateReason:
                updatedSensor.updateReason || cachedSensor.updateReason,
            };
          },
        );
      },
    }),
  );

  const notesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.timestamp'), dataKey: 'timestamp' },
    { label: i18n.t('Entity.note'), dataKey: 'note' },
    { label: i18n.t('Entity.createdBy'), dataKey: 'user_display_name' },
  ];

  const notesTableCellRenderer = (dataKey: string, note: UserNote) => {
    if (dataKey === 'timestamp') {
      return <DateCellRenderer value={note.timestamp} key={dataKey} />;
    } else {
      return <Table.TextCell key={dataKey}>{note[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <VStack gap="$2" height="100%">
      {errorMessage ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Sensor.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="sensor-error-banner"
        />
      ) : null}

      {sensor.isPending || sensorOverrides.isPending || device.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="sensor-loading" />
        </VStack>
      ) : null}

      {sensor.isSuccess && sensorOverrides.isSuccess && device.isSuccess ? (
        <VStack gap="$4">
          <SensorHeader
            sensor={sensor.data}
            sensorOverrides={sensorOverrides.data}
            deviceId={device.data?.deviceId || ''}
            isReadonly={!capabilities?.canUpdateSensor}
          />
          <DynamicTable
            testID="sensor-notes-table"
            isLoading={sensor.isLoading}
            hasLoadingError={sensor.isError}
            title={i18n.t('Page_Sensor.table_notes_title')}
            description={i18n.t('Page_Sensor.table_notes_description')}
            columnData={notesTableColumnData}
            rowData={sensor.data.userNotes || []}
            cellRenderer={notesTableCellRenderer}
            toolbarRightContent={
              capabilities?.canManageSensorNotes ? (
                <CreateUserNoteModal
                  trigger={
                    <Button
                      size="medium"
                      label={i18n.t('Page_Sensor.add_note')}
                      onPress={() => {}}
                      disabled={createNote.isPending}
                      testID="add-note-button"
                    />
                  }
                  onSuccess={async (newNote) => {
                    await createNote.mutateAsync({
                      sensorKey: sensor.data.key,
                      noteAddRequest: newNote,
                    });
                  }}
                />
              ) : null
            }
          />
        </VStack>
      ) : null}
    </VStack>
  );
}
